import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService, User, ApiError } from '../services/auth';
import { setAuthErrorCallback } from '../services/base';

/**
 * Optimized AuthContext that focuses solely on authentication
 * Subscription data is now managed by UnifiedSubscriptionContext
 */
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, confirmPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Optimized Auth Provider that only manages authentication state
 * Subscription data is handled by UnifiedSubscriptionContext
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  const handleApiError = (error: unknown) => {
    if (error && typeof error === 'object' && 'message' in error) {
      setError((error as ApiError).message);
    } else {
      setError('An unexpected error occurred');
    }
  };

  /**
   * Refresh user data from the API
   * Note: This no longer loads subscription data
   */
  const refreshUser = async () => {
    if (!authService.isAuthenticated()) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await authService.getCurrentUser();
      setUser(response.user);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // If token is invalid, clear it
      if (error && typeof error === 'object' && 'error' in error) {
        await logout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Login user
   * Subscription data will be loaded by UnifiedSubscriptionContext
   */
  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.login(email, password);
      setUser(response.user);
      
      // The unified subscription context will handle loading subscription data
    } catch (error) {
      handleApiError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Register new user
   */
  const register = async (email: string, password: string, confirmPassword: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.register(email, password, confirmPassword);
      
      // Check if user needs email verification
      if (response.user && !response.session) {
        // User registered but needs email verification
        setError('Account created successfully! Please check your email and click the verification link to complete your registration.');
        setUser(null);
      } else {
        // User registered and logged in successfully
        setUser(response.user);
      }
    } catch (error) {
      handleApiError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = async (): Promise<void> => {
    setIsLoading(true);
    
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setError(null);
      setIsLoading(false);
    }
  };

  /**
   * Handle auth errors (401s)
   */
  const handleAuthError = () => {
    console.log('Auth error detected - logging out user');
    setUser(null);
    setError('Your session has expired. Please log in again.');
    setIsLoading(false);
  };

  useEffect(() => {
    // Register the auth error callback
    setAuthErrorCallback(handleAuthError);
    
    // Initial user load
    refreshUser();
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};